import React from 'react';
import { linkifyWithLineBreaks } from '../../utils/linkify.tsx';
import { AppIcon, notificationTypeIcon } from '../../icons/appIcons.tsx';

interface Family {
  id: string;
  familyName: string;
  primaryContactFirstName: string;
  primaryContactLastName: string;
}

interface NotificationRead {
  id: string;
  familyId: string;
  readAt: string;
  family: Family;
}

interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'general' | 'payment' | 'event' | 'urgent' | 'maintenance';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  targetAudience: 'all' | 'active_families' | 'overdue_payments' | 'specific_families';
  specificFamilyIds: string | null;
  createdAt: string;
  updatedAt: string;
  expiresAt: string | null;
  isActive: boolean;
  creator: {
    firstName: string;
    lastName: string;
  };
  reads: NotificationRead[];
  targetFamilies?: Family[];
}

interface NotificationDetailViewProps {
  notification: Notification;
  onClose: () => void;
}

export default function NotificationDetailView({ notification, onClose }: NotificationDetailViewProps) {
  const getTypeColor = (type: string, priority: string) => {
    if (priority === 'urgent') return 'text-red-600 bg-red-50 border-red-200';
    
    switch (type) {
      case 'urgent': return 'text-red-600 bg-red-50 border-red-200';
      case 'payment': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'event': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'maintenance': return 'text-gray-600 bg-gray-50 border-gray-200';
      default: return 'text-green-600 bg-green-50 border-green-200';
    }
  };

  const getPriorityBadge = (priority: string) => {
    const colors = {
      urgent: 'bg-red-100 text-red-800',
      high: 'bg-orange-100 text-orange-800',
      normal: 'bg-blue-100 text-blue-800',
      low: 'bg-gray-100 text-gray-800'
    };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[priority as keyof typeof colors]}`}>
        {priority.charAt(0).toUpperCase() + priority.slice(1)} Priority
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTargetAudienceText = (targetAudience: string, specificFamilyIds: string | null) => {
    switch (targetAudience) {
      case 'all': return 'All Families';
      case 'active_families': return 'Active Families';
      case 'overdue_payments': return 'Families with Overdue Payments';
      case 'specific_families': {
        const familyCount = specificFamilyIds ? JSON.parse(specificFamilyIds).length : 0;
        return `${familyCount} Specific Families`;
      }
      default: return targetAudience;
    }
  };

  const targetFamilies = notification.targetFamilies || [];
  const readFamilyIds = new Set(notification.reads.map(read => read.familyId));
  const unreadFamilies = targetFamilies.filter(family => !readFamilyIds.has(family.id));
  const readFamilies = notification.reads.map(read => read.family).filter(family => family);

  const readPercentage = targetFamilies.length > 0 ? Math.round((readFamilies.length / targetFamilies.length) * 100) : 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-start mb-6">
            <div className="flex items-start space-x-3 flex-1">
              <AppIcon name={notificationTypeIcon(notification.type)} className="w-8 h-8 flex-shrink-0 mt-1 text-ggaa-green" />
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  {notification.title}
                </h2>
                <div className="flex items-center space-x-3 mb-2">
                  {getPriorityBadge(notification.priority)}
                  <span className="text-sm text-gray-500 capitalize">
                    {notification.type} Announcement
                  </span>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 ml-4 flex-shrink-0"
            >
              <AppIcon name="close" className="w-6 h-6" />
            </button>
          </div>

          {/* Message Content */}
          <div className={`border-l-4 ${getTypeColor(notification.type, notification.priority).split(' ')[2]} p-4 mb-6`}>
            <div className="prose prose-sm max-w-none">
              <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                {linkifyWithLineBreaks(notification.message)}
              </p>
            </div>
          </div>

          {/* Metadata and Stats */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Notification Details */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-900 mb-3">Notification Details</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Created:</span>
                  <span className="text-gray-900 font-medium">
                    {formatDate(notification.createdAt)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">From:</span>
                  <span className="text-gray-900 font-medium">
                    {notification.creator.firstName} {notification.creator.lastName}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Target Audience:</span>
                  <span className="text-gray-900 font-medium">
                    {getTargetAudienceText(notification.targetAudience, notification.specificFamilyIds)}
                  </span>
                </div>
                {notification.expiresAt && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Expires:</span>
                    <span className="text-gray-900 font-medium">
                      {formatDate(notification.expiresAt)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Read Statistics */}
            <div className="bg-blue-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-900 mb-3">Read Statistics</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Total Recipients:</span>
                  <span className="text-lg font-bold text-gray-900">{targetFamilies.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Families Read:</span>
                  <span className="text-lg font-bold text-green-600">{readFamilies.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Unread:</span>
                  <span className="text-lg font-bold text-orange-600">{unreadFamilies.length}</span>
                </div>
                <div className="mt-2">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-xs text-gray-500">Read Rate</span>
                    <span className="text-xs font-medium text-gray-700">{readPercentage}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-green-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${readPercentage}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Family Recipients */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Family Recipients</h3>
            
            {targetFamilies.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No family data available</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Families who have read */}
                {readFamilies.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-green-700 mb-3 flex items-center">
                      <span className="w-3 h-3 bg-green-500 rounded-full mr-2"></span>
                      Read ({readFamilies.length})
                    </h4>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {readFamilies.map(family => {
                        const readRecord = notification.reads.find(read => read.familyId === family.id);
                        return (
                          <div key={family.id} className="bg-white rounded-lg p-3 border border-green-200">
                            <div className="font-medium text-gray-900">{family.familyName}</div>
                            <div className="text-sm text-gray-600">
                              {family.primaryContactFirstName} {family.primaryContactLastName}
                            </div>
                            {readRecord && (
                              <div className="text-xs text-green-600 mt-1">
                                Read: {new Date(readRecord.readAt).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Families who haven't read */}
                {unreadFamilies.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-orange-700 mb-3 flex items-center">
                      <span className="w-3 h-3 bg-orange-500 rounded-full mr-2"></span>
                      Unread ({unreadFamilies.length})
                    </h4>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {unreadFamilies.map(family => (
                        <div key={family.id} className="bg-white rounded-lg p-3 border border-orange-200">
                          <div className="font-medium text-gray-900">{family.familyName}</div>
                          <div className="text-sm text-gray-600">
                            {family.primaryContactFirstName} {family.primaryContactLastName}
                          </div>
                          <div className="text-xs text-orange-600 mt-1">
                            Not yet read
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end mt-6">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
} 